// src/encounter/vaccine-administration-requests.tsx
// MIDDLE-column "Vaccination Administration Requests" section (PROD-1250). React port of Flutter
// pre_encounter_widget.dart vaccinationAdministrationRequest() (:1434) + VaccineAdministrationRow
// (vaccine_administartion_request_row.dart): a section header + one row per requested vaccination
// (title · "Requested on {date} by {name}" · a Give/Edit action). The HOST surface for the PROD-1227
// lot-aware administration editor — the row action raises `onAdminister`; opening + saving the editor
// is the PROD-1227 composition seam (a live PHI write), kept out of this host.
//
// PRESENTATIONAL: takes the mapped view models (to-vaccination-requests). HIDDEN when there are no
// requests (Flutter body() gate: length>0 ? block : Container()). DS atoms + tokens only — no
// hardcoded hex, no invented chrome. The per-row action label mirrors Flutter: isAdministrationRequested
// → "Give" (nurse administers); otherwise → "Edit" (doctor amends the request).
import { Button, EncounterSectionHeader } from '@scripsteam/scrips-signal-ds';
import type { VaccineAdministrationRequestView } from './lib/to-vaccination-requests';
import { useDesignV2 } from './lib/design-v2';
import { EncBlock, EncSectionCard } from './enc-section-card';

export interface VaccineAdministrationRequestsProps {
  requests: VaccineAdministrationRequestView[];
  /** Open the administration editor for a request (PROD-1227 seam). Omitted → rows render
   *  display-only (no action button), so the host call-site need not wire the editor yet. */
  onAdminister?: (request: VaccineAdministrationRequestView) => void;
}

const CARD =
  'bg-surface-card border border-border-default ' +
  'rounded-lg p-6';
const ROW =
  'flex items-center justify-between gap-3 p-3 ' +
  // Vaccines ride the medications rail in Flutter (medicationsColor accent); map to a brand
  // accent token (no hardcoded hex). `--color-surface-subtle` has no DS token today, so the
  // fallback the old inline style leaned on is inlined directly as the surface-card colour.
  'border-l-3 border-l-brand-primary rounded-md ' +
  'bg-surface-card';
const TITLE = 'text-size-sm font-semibold text-text-primary';
const META = 'mt-1 text-size-xs text-text-hint';
const STACK_2 = 'flex flex-col gap-2';

/** no-Z ISO → a short readable date; falls back to the raw string when unparseable. */
function formatRequestedOn(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** "Requested on {date} by {name}" — drops whichever part is missing (Flutter shows a "-" for name). */
function requestedMeta(req: VaccineAdministrationRequestView): string {
  const on = formatRequestedOn(req.requestedOn);
  const by = req.requestedBy ?? '—';
  return on ? `Requested on ${on} by ${by}` : `Requested by ${by}`;
}

export function VaccineAdministrationRequests({ requests, onAdminister }: VaccineAdministrationRequestsProps) {
  const designV2 = useDesignV2();
  // No requests → section hidden (Flutter parity: the whole block is gated on length > 0).
  if (requests.length === 0) return null;

  // Frame v2 (design export 21-10, EncCoreVaccineRequests): one Plan card, one block, hairline-separated rows.
  if (designV2) {
    return (
      <EncSectionCard section="p" aria-label="Vaccination administration requests">
        <EncBlock title="Vaccination administration requests" icon="pill">
          <div data-slot="pv-stack">
            {requests.map((req) => (
              <div key={req.id} data-slot="pv-row">
                <div data-slot="pv-grow">
                  <div data-slot="pv-title">{req.title || 'Vaccination'}</div>
                  <p data-slot="pv-meta">{requestedMeta(req)}</p>
                </div>
                {onAdminister && (
                  <Button
                    variant={req.isAdministrationRequested ? 'primary' : 'secondary'}
                    size="sm"
                    onClick={() => onAdminister(req)}
                  >
                    {req.isAdministrationRequested ? 'Give' : 'Edit'}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </EncBlock>
      </EncSectionCard>
    );
  }

  return (
    <section className={CARD} aria-label="Vaccination administration requests">
      <EncounterSectionHeader title="Vaccination Administration Requests" variant="section" />
      <div className="h-3" />
      <div className={STACK_2}>
        {requests.map((req) => (
          <div key={req.id} className={ROW}>
            <div className="min-w-0">
              <div className={TITLE}>{req.title || 'Vaccination'}</div>
              <p className={META}>{requestedMeta(req)}</p>
            </div>
            {onAdminister && (
              <Button
                variant={req.isAdministrationRequested ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => onAdminister(req)}
              >
                {req.isAdministrationRequested ? 'Give' : 'Edit'}
              </Button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
